import { supabase, getCurrentUser } from './supabase';
import type { CarContact, CarContactCategory } from '../types';

const TABLE = 'car_contacts';

export const CAR_CONTACT_CATEGORIES: CarContactCategory[] = [
  'Workshop',
  'Parts',
  'Dealer',
  'Insurance',
  'Person',
  'Other',
];

type CarContactInput = {
  name: string;
  category: CarContactCategory;
  phone: string;
  website?: string;
  notes?: string;
};

type CarContactUpdateInput = Partial<CarContactInput>;

function sanitizeInput(input: CarContactInput | CarContactUpdateInput): CarContactInput | CarContactUpdateInput {
  const payload: CarContactUpdateInput = {};

  if (typeof input.name === 'string') payload.name = input.name.trim();
  if (typeof input.category === 'string') payload.category = input.category;
  if (typeof input.phone === 'string') payload.phone = input.phone.trim();

  if (typeof input.website === 'string') {
    const website = input.website.trim();
    payload.website = website || undefined;
  }

  if (typeof input.notes === 'string') {
    const notes = input.notes.trim();
    payload.notes = notes || undefined;
  }

  return payload;
}

export async function fetchCarContacts(): Promise<CarContact[]> {
  const user = await getCurrentUser();
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('user_id', user.id)
    .order('name', { ascending: true });

  if (error) throw error;
  return (data || []) as CarContact[];
}

export async function createCarContact(input: CarContactInput): Promise<CarContact> {
  const user = await getCurrentUser();
  if (!user) throw new Error('User not authenticated');

  const payload = sanitizeInput(input) as CarContactInput;
  const { data, error } = await supabase
    .from(TABLE)
    .insert({
      user_id: user.id,
      ...payload,
      website: payload.website || null,
      notes: payload.notes || null,
    })
    .select()
    .single();

  if (error) throw error;
  return data as CarContact;
}

export async function updateCarContact(id: string, input: CarContactUpdateInput): Promise<CarContact> {
  const payload = sanitizeInput(input) as CarContactUpdateInput;
  const { data, error } = await supabase
    .from(TABLE)
    .update({
      ...payload,
      website: payload.website || null,
      notes: payload.notes || null,
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as CarContact;
}

export async function deleteCarContact(id: string): Promise<void> {
  const { error } = await supabase
    .from(TABLE)
    .delete()
    .eq('id', id);
  if (error) throw error;
}
